import React, { useState, useRef, useEffect } from 'react';

function AutocompleteInput({
  value,
  onChange,
  onSuggestionSelect,
  suggestions = [],
  showSuggestions = true,
  placeholder = "Start typing...",
  className = "",
  style = {},
  error = null
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Show suggestions when there are results and input is focused
  useEffect(() => {
    if (showSuggestions && suggestions.length > 0 && document.activeElement === inputRef.current) {
      setIsOpen(true);
    } else if (suggestions.length === 0) {
      setIsOpen(false);
    }
  }, [suggestions, showSuggestions]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);
    setHighlightedIndex(-1);
    
    if (newValue.length > 0 && suggestions.length > 0) {
      setIsOpen(true);
    }
  };

  const handleInputFocus = () => {
    if (suggestions.length > 0) {
      setIsOpen(true);
    }
  };

  const handleKeyDown = (e) => {
    if (!isOpen || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          selectSuggestion(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  const selectSuggestion = (suggestion) => {
    const selectedText = suggestion.displayText || suggestion.city || suggestion;
    onChange(selectedText);
    setIsOpen(false);
    setHighlightedIndex(-1);
    
    if (onSuggestionSelect) {
      onSuggestionSelect(suggestion);
    }
  };

  const getMatchTypeColor = (matchType) => {
    switch (matchType) {
      case 'exact':
      case 'alias_exact':
        return 'text-green-600';
      case 'starts_with':
      case 'alias_starts':
        return 'text-blue-600';
      case 'popular':
        return 'text-purple-600';
      default:
        return 'text-gray-600';
    }
  };

  const getMatchTypeLabel = (matchType) => {
    switch (matchType) {
      case 'exact':
        return '✓ Exact match';
      case 'alias_exact':
        return '✓ Perfect match';
      case 'starts_with':
        return '→ Starts with';
      case 'alias_starts':
        return '→ Alias match';
      case 'popular':
        return '⭐ Popular';
      default:
        return '';
    }
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={`w-full px-4 py-3 border-2 border-gray-200 focus:border-blue-500 focus:outline-none transition-colors ${
          error ? 'border-red-500 bg-red-50' : ''
        } ${className}`}
        style={style}
        autoComplete="off"
      />

      {error && (
        <div className="mt-1 text-sm text-red-600">
          {error}
        </div>
      )}

      {/* Suggestions dropdown */}
      {isOpen && suggestions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 shadow-lg max-h-64 overflow-y-auto"
          style={{ backgroundColor: '#ffffff' }}
        >
          {suggestions.map((suggestion, index) => {
            const isHighlighted = index === highlightedIndex;
            const city = suggestion.displayText || suggestion.city || suggestion;
            const matchType = suggestion.matchType;
            const score = suggestion.score;

            return (
              <div
                key={`${city}-${index}`}
                className={`px-4 py-3 cursor-pointer transition-colors ${
                  isHighlighted 
                    ? 'bg-blue-50 border-l-4 border-blue-500' 
                    : 'hover:bg-gray-50 border-l-4 border-transparent'
                }`}
                onClick={() => selectSuggestion(suggestion)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <div className="flex justify-between items-center">
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{city}</div>
                    {matchType && (
                      <div className={`text-xs ${getMatchTypeColor(matchType)}`}>
                        {getMatchTypeLabel(matchType)}
                        {score && ` (${score})`}
                      </div>
                    )}
                  </div>
                  {isHighlighted && (
                    <div className="text-blue-500 text-sm">
                      Press Enter
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default AutocompleteInput; 